import { useEffect, useRef, useState } from 'react';

type UseStateList<T> = {
  step: T;
  next(): void;
  prev(): void;
  setStep(item: T): void;
  currentIndex: number;
};

export const useStateList = <T>(list: T[], initialItem?: T): UseStateList<T> => {
  const initialIndex = list.findIndex(listItem => listItem === initialItem);
  const [currentIndex, setCurrentIndex] = useState(initialIndex === -1 ? 0 : initialIndex);

  useEffect(() => {
    initialIndex !== -1 && setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const next = () => {
    setCurrentIndex(prevIndex => {
      if (prevIndex < list.length - 1) {
        return prevIndex + 1;
      }
      return prevIndex;
    });
  };

  const prev = () => {
    setCurrentIndex(prevIndex => {
      if (prevIndex > 0) {
        return prevIndex - 1;
      }
      return prevIndex;
    });
  };

  const setStep = (item: T) => {
    const index = list.indexOf(item);
    if (index !== -1) {
      setCurrentIndex(index);
    } else {
      console.error(`Item "${item}" not found in the list.`);
    }
  };

  const step = list[currentIndex];

  return { step, next, prev, setStep, currentIndex };
};

export const useStateValueList = <T extends string | number>(list: T[], initialItem?: T): UseStateList<T> => {
  const listRef = useRef(list);
  const [currentValue, setCurrentValue] = useState<T>(initialItem || list[0]);
  const currentIndex = listRef.current.indexOf(currentValue);

  useEffect(() => {
    listRef.current = list;
  }, [list]);

  const next = () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < listRef.current.length) {
      setCurrentValue(listRef.current[nextIndex]);
    }
  };

  const prev = () => {
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      setCurrentValue(listRef.current[prevIndex]);
    }
  };

  const setStep = (item: T) => {
    const index = listRef.current.indexOf(item);
    if (index !== -1) {
      setCurrentValue(item);
    } else {
      console.error(`Item "${item}" not found in the list.`);
    }
    return index;
  };

  return { step: currentValue, next, prev, setStep, currentIndex };
};
